import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { AuthProvider, useAuth } from "./context/AuthContext";

import LandingPage from "./pages/LandingPage";
import CoursesPage from "./pages/CoursesPage";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CompanyAdminPage from "./pages/CompanyAdminPage";
import MethodologyGuidePage from "./pages/MethodologyGuidePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import AdminInvoicesPage from "./pages/AdminInvoicesPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminPlansPage from "./pages/AdminPlansPage";
import AdminTrashPage from "./pages/AdminTrashPage";
import AdminHappinessPage from "./pages/AdminHappinessPage";
import AdminHappinessStatsPage from "./pages/AdminHappinessStatsPage";
import SurveyFillPage from "./pages/SurveyFillPage";
import SurveyStatisticsPage from "./pages/SurveyStatisticsPage";
import AdminCompanyDetailPage from "./pages/AdminCompanyDetailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PublicSurveyPage from "./pages/PublicSurveyPage";
import SurveyClosedPage from "./pages/SurveyClosedPage";
import MyTasksPage from "./pages/MyTasksPage";
import CompanyQuestionsPage from "./pages/CompanyQuestionsPage";
import AppLayout from "./components/AppLayout";

function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="label-caps">A carregar...</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
    return children;
}

function App() {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    const inner = (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" />
                <Routes>
                    {/* Public */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/planos" element={<PricingPage />} />
                    <Route path="/cursos" element={<CoursesPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/s/:token" element={<PublicSurveyPage />} />
                    <Route path="/s/:token/closed" element={<SurveyClosedPage />} />

                    {/* App */}
                    <Route
                        path="/app"
                        element={
                            <ProtectedRoute>
                                <AppLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/app/dashboard" replace />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="my-tasks" element={<MyTasksPage />} />
                        <Route
                            path="questions"
                            element={
                                <ProtectedRoute roles={["company_manager", "happiness_manager", "super_admin"]}>
                                    <CompanyQuestionsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="projects" element={<ProjectsPage />} />
                        <Route path="projects/:id" element={<ProjectDetailPage />} />
                        <Route path="projects/:id/surveys/:phase" element={<SurveyFillPage />} />
                        <Route path="projects/:id/surveys/:phase/closed" element={<SurveyClosedPage />} />
                        <Route path="projects/:id/surveys/:phase/statistics" element={<SurveyStatisticsPage />} />
                        <Route
                            path="metodologia"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <MethodologyGuidePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="subscription"
                            element={
                                <ProtectedRoute roles={["company_manager", "super_admin"]}>
                                    <SubscriptionPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="company"
                            element={
                                <ProtectedRoute roles={["company_manager", "super_admin"]}>
                                    <CompanyAdminPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <SuperAdminPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/companies/:id"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminCompanyDetailPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/invoices"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminInvoicesPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/dashboard"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminDashboardPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/plans"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminPlansPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/trash"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminTrashPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/happiness-admin"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminHappinessPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="super/happiness-stats"
                            element={
                                <ProtectedRoute roles={["super_admin"]}>
                                    <AdminHappinessStatsPage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
    if (!siteKey) {
        // No site key configured (e.g. local dev) — render without provider.
        return inner;
    }
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={siteKey}
            scriptProps={{ async: true, defer: true, appendTo: "head" }}
            container={{ parameters: { badge: "bottomright", theme: "light" } }}
        >
            {inner}
        </GoogleReCaptchaProvider>
    );
}

export default App;
